import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  ArrowUpRight,
  CalendarOff,
  Check,
  ChevronDown,
  Inbox,
  TimerReset,
  X,
} from 'lucide-react'
import { Badge, Button, Input, SegmentedControl } from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import { cn } from '@/lib/cn'
import type {
  OrganizationDashboardPendingCorrectionItem,
  OrganizationDashboardPendingLeaveItem,
} from '@/types/dashboard.types'
import { leaveKeys, useLeaveDecision } from '@/features/leave/hooks/useLeaves'
import { TimeCorrectionRequestDetailModal } from '@/features/attendance/components/TimeCorrectionRequestDetailModal'
import {
  useApproveTimeCorrectionRequest,
  useRejectTimeCorrectionRequest,
} from '@/features/attendance/hooks/useTimeCorrection'
import { invalidateDashboards } from '../../hooks/useDashboardStats'
import { DashboardTile } from './DashboardTile'
import { formatClock } from './dashboardFormat'

const COLLAPSED_ROWS = 6
const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

type QueueFilter = 'all' | 'leave' | 'correction'

type QueueItem =
  | { kind: 'leave'; item: OrganizationDashboardPendingLeaveItem }
  | { kind: 'correction'; item: OrganizationDashboardPendingCorrectionItem }

/** A reason of only punctuation (".", "-") says nothing; treat it as missing. */
function meaningful(text: string | undefined): string | null {
  const trimmed = text?.trim() ?? ''
  return /[\p{L}\p{N}]/u.test(trimmed) ? trimmed : null
}

function leaveTypeLabel(type: string): string {
  const words = type.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function dayMonth(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(
      new Date(iso)
    )
  } catch {
    return iso
  }
}

function weekdayDayMonth(iso: string, timezone?: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: timezone || undefined,
    }).format(new Date(iso))
  } catch {
    return ''
  }
}

/** Inclusive calendar days between two ISO dates. */
function leaveDays(startIso: string, endIso: string): number {
  const start = new Date(startIso.slice(0, 10)).getTime()
  const end = new Date(endIso.slice(0, 10)).getTime()
  return Number.isFinite(start) && Number.isFinite(end)
    ? Math.max(1, Math.round((end - start) / DAY_MS) + 1)
    : 1
}

function waitingFor(createdAt: string, now: number): { label: string; tone: string } {
  const age = Math.max(0, now - new Date(createdAt).getTime())
  let label: string
  if (age < HOUR_MS) label = `${Math.max(1, Math.round(age / 60000))}m`
  else if (age < DAY_MS) label = `${Math.round(age / HOUR_MS)}h`
  else label = `${Math.floor(age / DAY_MS)}d`
  let tone = 'text-gray-500 dark:text-gray-400'
  if (age >= 7 * DAY_MS) tone = 'text-[#d92d50] dark:text-rose-300'
  else if (age >= 2 * DAY_MS) tone = 'text-amber-700 dark:text-amber-400'
  return { label, tone }
}

function TimeChip({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-md bg-primary/6 px-2 py-1 dark:bg-sky-400/10">
      <span className="text-[11px] text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-xs font-semibold tabular-nums text-primary dark:text-sky-300">
        {value}
      </span>
    </span>
  )
}

function CorrectionDetail({
  item,
  timezone,
}: {
  item: OrganizationDashboardPendingCorrectionItem
  timezone?: string
}): React.ReactElement {
  const reason = meaningful(item.reason)
  const day = item.requestedClockIn ?? item.requestedClockOut
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {day ? (
          <span className="mr-0.5 text-xs font-medium text-gray-700 dark:text-gray-200">
            {weekdayDayMonth(day, timezone)}
          </span>
        ) : null}
        {item.requestedClockIn ? (
          <TimeChip label="In" value={formatClock(item.requestedClockIn, timezone)} />
        ) : null}
        {item.requestedClockIn && item.requestedClockOut ? (
          <ArrowRight className="h-3 w-3 text-gray-300 dark:text-gray-600" aria-hidden />
        ) : null}
        {item.requestedClockOut ? (
          <TimeChip label="Out" value={formatClock(item.requestedClockOut, timezone)} />
        ) : null}
        {!item.requestedClockIn && !item.requestedClockOut ? (
          <span className="text-xs text-gray-500 dark:text-gray-400">Time change</span>
        ) : null}
      </div>
      <p
        className={cn(
          'line-clamp-2 text-xs leading-5',
          reason ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'
        )}
        title={reason ?? undefined}
      >
        {reason ?? 'No reason given'}
      </p>
    </div>
  )
}

function LeaveDetail({
  item,
}: {
  item: OrganizationDashboardPendingLeaveItem
}): React.ReactElement {
  const days = leaveDays(item.startDate, item.endDate)
  const sameDay = item.startDate.slice(0, 10) === item.endDate.slice(0, 10)
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center rounded-md bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-800 dark:bg-sky-400/10 dark:text-sky-300">
        {leaveTypeLabel(item.leaveType)}
      </span>
      <span className="text-xs font-medium text-gray-700 tabular-nums dark:text-gray-200">
        {sameDay
          ? dayMonth(item.startDate)
          : `${dayMonth(item.startDate)} – ${dayMonth(item.endDate)}`}
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        · {days} {days === 1 ? 'day' : 'days'}
      </span>
    </div>
  )
}

const KIND_META = {
  leave: {
    label: 'Leave',
    icon: CalendarOff,
    tile: 'bg-sky-100 text-sky-700 dark:bg-sky-400/15 dark:text-sky-300',
  },
  correction: {
    label: 'Time correction',
    icon: TimerReset,
    tile: 'bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300',
  },
} as const

/** Pending leave and time-correction requests, approvable without leaving the dashboard. */
export function ActionQueueCard({
  leaves,
  corrections,
  companyName,
  timezone,
}: {
  leaves: OrganizationDashboardPendingLeaveItem[]
  corrections: OrganizationDashboardPendingCorrectionItem[]
  companyName: (id: string) => string
  timezone?: string
}): React.ReactElement {
  const queryClient = useQueryClient()
  const leaveDecision = useLeaveDecision()
  const approveCorrection = useApproveTimeCorrectionRequest()
  const rejectCorrection = useRejectTimeCorrectionRequest()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [selectedCorrection, setSelectedCorrection] =
    useState<OrganizationDashboardPendingCorrectionItem | null>(null)
  const [reason, setReason] = useState('')
  const [filter, setFilter] = useState<QueueFilter>('all')
  const [expanded, setExpanded] = useState(false)
  const [now] = useState(() => Date.now())

  const refresh = (): void => {
    // Corrections hooks only refresh the org dashboard; cover company pages too.
    void invalidateDashboards(queryClient)
  }
  const refreshWorkspaceLeaves = (): void => {
    void queryClient.invalidateQueries({ queryKey: leaveKeys.workspaceRoot })
  }
  const startReject = (id: string): void => {
    setRejectingId(id)
    setReason('')
  }
  const cancelReject = (): void => {
    setRejectingId(null)
    setReason('')
  }

  const approveLeave = (item: OrganizationDashboardPendingLeaveItem): void => {
    setBusyId(item.id)
    leaveDecision.mutate(
      { companyId: item.companyId, leaveId: item.id, body: { approve: true } },
      { onSuccess: refresh, onSettled: () => setBusyId(null) }
    )
  }
  const confirmRejectLeave = (item: OrganizationDashboardPendingLeaveItem): void => {
    setBusyId(item.id)
    leaveDecision.mutate(
      {
        companyId: item.companyId,
        leaveId: item.id,
        body: { approve: false, note: reason.trim() || undefined },
      },
      {
        onSuccess: () => {
          refresh()
          cancelReject()
        },
        onSettled: () => setBusyId(null),
      }
    )
  }
  const approveCorr = (item: OrganizationDashboardPendingCorrectionItem): void => {
    setBusyId(item.id)
    approveCorrection.mutate(
      { companyId: item.companyId, requestId: item.id },
      { onSuccess: refresh, onSettled: () => setBusyId(null) }
    )
  }
  const confirmRejectCorr = (item: OrganizationDashboardPendingCorrectionItem): void => {
    if (!reason.trim()) {
      toast.error('A reason is required to reject a time correction.')
      return
    }
    setBusyId(item.id)
    rejectCorrection.mutate(
      { companyId: item.companyId, requestId: item.id, body: { rejectionReason: reason.trim() } },
      {
        onSuccess: () => {
          refresh()
          cancelReject()
        },
        onSettled: () => setBusyId(null),
      }
    )
  }

  // One queue, oldest first, whatever the kind.
  const queue: QueueItem[] = [
    ...leaves.map((item) => ({ kind: 'leave' as const, item })),
    ...corrections.map((item) => ({ kind: 'correction' as const, item })),
  ].sort((a, b) => a.item.createdAt.localeCompare(b.item.createdAt))
  const filtered = queue.filter((entry) => filter === 'all' || entry.kind === filter)
  const visible = expanded ? filtered : filtered.slice(0, COLLAPSED_ROWS)
  const hidden = filtered.length - visible.length
  const total = queue.length
  const oldest = queue[0] ? waitingFor(queue[0].item.createdAt, now) : null

  const summary = [
    corrections.length > 0
      ? `${corrections.length} ${corrections.length === 1 ? 'correction' : 'corrections'}`
      : null,
    leaves.length > 0
      ? `${leaves.length} leave ${leaves.length === 1 ? 'request' : 'requests'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  function renderRow(entry: QueueItem): React.ReactElement {
    const { kind, item } = entry
    const meta = KIND_META[kind]
    const KindIcon = meta.icon
    const busy = busyId === item.id
    const rejecting = rejectingId === item.id
    const reasonRequired = kind === 'correction'
    const onApprove = (): void =>
      kind === 'leave' ? approveLeave(entry.item) : approveCorr(entry.item)
    const onConfirmReject = (): void =>
      kind === 'leave' ? confirmRejectLeave(entry.item) : confirmRejectCorr(entry.item)
    const name = item.userName || item.employeeId || 'Unknown'
    const waiting = waitingFor(item.createdAt, now)

    return (
      <li
        key={item.id}
        className={cn(
          'group px-5 py-3.5 transition-colors',
          rejecting
            ? 'bg-rose-50/60 dark:bg-rose-950/20'
            : 'hover:bg-gray-50/70 dark:hover:bg-gray-700/20'
        )}
      >
        <div className="grid items-center gap-x-5 gap-y-3 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_auto]">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative shrink-0">
              <Avatar name={name} size="lg" tone="soft" />
              <span
                className={cn(
                  'absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white dark:ring-gray-800',
                  meta.tile
                )}
                title={meta.label}
              >
                <KindIcon className="h-3 w-3" aria-hidden />
              </span>
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                {name}
              </p>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                <span className="sr-only">{meta.label} · </span>
                {companyName(item.companyId)}
              </p>
            </div>
          </div>

          {kind === 'leave' ? (
            <LeaveDetail item={entry.item} />
          ) : (
            <CorrectionDetail item={entry.item} timezone={timezone} />
          )}

          <div className="flex items-center justify-between gap-4 md:justify-end">
            <span
              className={cn('text-xs tabular-nums', waiting.tone)}
              title={`Requested ${new Date(item.createdAt).toLocaleString()}`}
            >
              Waiting {waiting.label}
            </span>
            {rejecting ? null : (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={onApprove}
                  loading={busy}
                >
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  <span className="ml-1">Approve</span>
                </Button>
                <button
                  type="button"
                  onClick={() => startReject(item.id)}
                  disabled={busy}
                  aria-label={`Reject ${name}'s ${meta.label.toLowerCase()}`}
                  title="Reject"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-rose-50 hover:text-[#d92d50] disabled:opacity-50 dark:text-gray-400 dark:hover:bg-rose-400/10 dark:hover:text-rose-300"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
                {kind === 'leave' ? (
                  <Link
                    to={`/leaves?companyId=${item.companyId}`}
                    onClick={refreshWorkspaceLeaves}
                    aria-label={`Open ${name}'s leave`}
                    title="Open"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-sky-300"
                  >
                    <ArrowUpRight className="h-4 w-4" aria-hidden />
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSelectedCorrection(entry.item)}
                    disabled={busy}
                    aria-label={`Open ${name}'s time correction`}
                    aria-haspopup="dialog"
                    title="Review time correction"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-sky-300"
                  >
                    <ArrowUpRight className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {rejecting ? (
          <form
            className="mt-3 flex flex-wrap items-center gap-2 md:pl-13"
            onSubmit={(e) => {
              e.preventDefault()
              onConfirmReject()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancelReject()
            }}
          >
            <Input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                reasonRequired
                  ? 'Why is this rejected? (required)'
                  : 'Add a note for the employee (optional)'
              }
              aria-label="Rejection reason"
              autoFocus
              className="h-9 min-w-0 flex-1 sm:max-w-md"
            />
            <Button type="submit" size="sm" variant="secondary" loading={busy}>
              Reject request
            </Button>
            <button
              type="button"
              onClick={cancelReject}
              className="px-2 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </form>
        ) : null}
      </li>
    )
  }

  const showFilter = leaves.length > 0 && corrections.length > 0

  return (
    <DashboardTile
      title="Action queue"
      icon={Inbox}
      subtitle={
        <>
          {summary}
          {oldest ? (
            <>
              {' · '}
              <span className={oldest.tone}>oldest waiting {oldest.label}</span>
            </>
          ) : null}
        </>
      }
      actions={
        <div className="flex items-center gap-3">
          {showFilter ? (
            <div className="hidden sm:block">
              <SegmentedControl
                options={[
                  { value: 'all' as const, label: `All ${total}` },
                  { value: 'correction' as const, label: 'Corrections' },
                  { value: 'leave' as const, label: 'Leave' },
                ]}
                value={filter}
                onChange={(next) => {
                  setFilter(next)
                  setExpanded(false)
                }}
                ariaLabel="Filter requests"
                size="sm"
              />
            </div>
          ) : null}
          {/* Narrow screens: the filter would crush the title; show the count instead. */}
          <Badge variant="warning" size="sm" className={showFilter ? 'sm:hidden' : undefined}>
            {total} pending
          </Badge>
        </div>
      }
      bodyClassName="border-t border-gray-100 dark:border-gray-700/70"
    >
      <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">{visible.map(renderRow)}</ul>
      {hidden > 0 || (expanded && filtered.length > COLLAPSED_ROWS) ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-center gap-1.5 rounded-b-[var(--radius-panel)] border-t border-gray-100 px-4 py-2.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-50 dark:border-gray-700/70 dark:text-gray-400 dark:hover:bg-gray-700/40"
        >
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')}
            aria-hidden
          />
          {expanded ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      ) : null}
      {selectedCorrection ? (
        <TimeCorrectionRequestDetailModal
          companyId={selectedCorrection.companyId}
          requestId={selectedCorrection.id}
          companyName={companyName(selectedCorrection.companyId)}
          onClose={() => setSelectedCorrection(null)}
          onActionComplete={refresh}
        />
      ) : null}
    </DashboardTile>
  )
}
